import React, { useState, useRef } from 'react';
import Navbar from "../../layout/Navbar";
import Footer from "../../layout/Footer";
import { Upload, X, CheckCircle, AlertCircle, Pen, Image as ImageIcon, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

const TreatmentEstimateForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenNumber, setTokenNumber] = useState('');
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState('');

  // Signature refs and state
  const sigCanvas = useRef(null);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [signatureDataURL, setSignatureDataURL] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const applicantSigCanvas = useRef(null);
  const [applicantSignatureMode, setApplicantSignatureMode] = useState('draw');
  const [applicantSignatureDataURL, setApplicantSignatureDataURL] = useState(null);
  const [applicantSignatureFile, setApplicantSignatureFile] = useState(null);


  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    // Reference & Date Information
    // refNumber: '',
    // date: getTodayDate(),
    // mobileNumber: '',
    // emailId: '',

    // Patient Information
    patientName: '',
    age: '',
    sex: '',
    fatherHusbandName: '',
    fullAddress: '',
    opdHospitalNumber: '',

    // Consultant & Diagnosis Information
    consultantName: '',
    unit: '',
    provisionalDiagnosis: '',
    operationDate: '',
    proposedOperation: '',

    // Cost Breakdown
    routineInvestigationCost: '',
    specialisedInvestigationCost: '',
    specialisedInvestigationDetails: '',
    medicinesSurgeryCost: '',

    // Calculated Total (auto-calculated)
    totalCost: 0
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-calculate total cost when cost fields change
    if (['routineInvestigationCost', 'specialisedInvestigationCost', 'medicinesSurgeryCost'].includes(name)) {
      calculateTotalCost(name, value);
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const calculateTotalCost = (fieldName, fieldValue) => {
    const currentValues = { ...formData };
    currentValues[fieldName] = fieldValue;

    const routine = parseFloat(currentValues.routineInvestigationCost) || 0;
    const specialised = parseFloat(currentValues.specialisedInvestigationCost) || 0;
    const medicines = parseFloat(currentValues.medicinesSurgeryCost) || 0;

    const total = routine + specialised + medicines;

    setFormData(prev => ({
      ...prev,
      totalCost: total
    }));
  };

  // Consultant Signature handling functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setSignatureDataURL(null);
    setSignatureFile(null);
    setErrors(prev => ({ ...prev, consultantSignature: '' }));
  };

  const saveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataURL = sigCanvas.current.toDataURL('image/png');
      setSignatureDataURL(dataURL);
      setErrors(prev => ({ ...prev, consultantSignature: '' }));
    } else {
      setErrors(prev => ({ ...prev, consultantSignature: 'Please draw your signature / कृपया अपना हस्ताक्षर करें' }));
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, consultantSignature: 'File size should be less than 2MB' }));
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, consultantSignature: 'Only JPG and PNG images allowed' }));
        return;
      }

      setSignatureFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureDataURL(reader.result);
        setErrors(prev => ({ ...prev, consultantSignature: '' }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Applicant signature handling functions
  const clearApplicantSignature = () => {
    if (applicantSigCanvas.current) {
      applicantSigCanvas.current.clear();
    }
    setApplicantSignatureDataURL(null);
    setApplicantSignatureFile(null);
    setErrors(prev => ({ ...prev, applicantSignature: '' }));
  };

  const saveApplicantSignature = () => {
    if (applicantSigCanvas.current && !applicantSigCanvas.current.isEmpty()) {
      const dataURL = applicantSigCanvas.current.toDataURL('image/png');
      setApplicantSignatureDataURL(dataURL);
      setErrors(prev => ({ ...prev, applicantSignature: '' }));
    } else {
      setErrors(prev => ({ ...prev, applicantSignature: 'Please draw your signature' }));
    }
  };

  const handleApplicantSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, applicantSignature: 'File size should be less than 2MB' }));
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, applicantSignature: 'Only JPG and PNG images allowed' }));
        return;
      }

      setApplicantSignatureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setApplicantSignatureDataURL(reader.result);
        setErrors(prev => ({ ...prev, applicantSignature: '' }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.patientName.trim()) newErrors.patientName = 'Required / आवश्यक';
    if (!formData.age.trim()) newErrors.age = 'Required / आवश्यक';
    if (!formData.sex) newErrors.sex = 'Required / आवश्यक';
    if (!formData.fatherHusbandName.trim()) newErrors.fatherHusbandName = 'Required / आवश्यक';
    if (!formData.fullAddress.trim()) newErrors.fullAddress = 'Required / आवश्यक';
    if (!formData.opdHospitalNumber.trim()) newErrors.opdHospitalNumber = 'Required / आवश्यक';

    if (!formData.consultantName.trim()) newErrors.consultantName = 'Required / आवश्यक';
    if (!formData.provisionalDiagnosis.trim()) newErrors.provisionalDiagnosis = 'Required / आवश्यक';
    if (!formData.proposedOperation.trim()) newErrors.proposedOperation = 'Required / आवश्यक';

    // Mobile number validation
    // if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber)) {
    //   newErrors.mobileNumber = 'Invalid Mobile Number / अमान्य मोबाइल नंबर';
    // }

    // Email validation
    // if (formData.emailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId)) {
    //   newErrors.emailId = 'Invalid Email / अमान्य ईमेल';
    // }

    // Signature validation
    if (!signatureDataURL) {
      newErrors.consultantSignature = 'Consultant signature is required / चिकित्सक का हस्ताक्षर आवश्यक है';
    }

    // Applicant Signature validation - NEW
    if (!applicantSignatureDataURL) {
      newErrors.applicantSignature = 'Applicant signature is required/ आवेदक का हस्ताक्षर आवश्यक है';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstError = document.querySelector('.border-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();

      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      // Append consultant signature
      if (signatureMode === 'draw' && signatureDataURL) {
        const response = await fetch(signatureDataURL);
        const blob = await response.blob();
        submitData.append('consultantSignature', blob, 'consultant-signature.png');
      } else if (signatureMode === 'upload' && signatureFile) {
        submitData.append('consultantSignature', signatureFile);
      }

      // Append applicant signature
      if (applicantSignatureMode === 'draw' && applicantSignatureDataURL) {
        const response = await fetch(applicantSignatureDataURL);
        const blob = await response.blob();
        submitData.append('applicantSignature', blob, 'applicant-signature.png');
      } else if (applicantSignatureMode === 'upload' && applicantSignatureFile) {
        submitData.append('applicantSignature', applicantSignatureFile);
      }

      const response = await fetch('https://wellnessplix.in/formProxy.php?endpoint=treatment-estimate', {
        method: 'POST',
        body: submitData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit form');
      }

      if (result.success) {
        setTokenNumber(result.data.submissionNo);
        setPdfUrl(result.data.pdfUrl);
        setSuccess(true);
      } else {
        throw new Error(result.message || 'Submission failed');
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: error.message || 'Failed to submit form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      // refNumber: '',
      // date: '',
      // mobileNumber: '',
      // emailId: '',
      patientName: '',
      age: '',
      sex: '',
      fatherHusbandName: '',
      fullAddress: '',
      opdHospitalNumber: '',
      consultantName: '',
      unit: '',
      provisionalDiagnosis: '',
      operationDate: '',
      proposedOperation: '',
      routineInvestigationCost: '',
      specialisedInvestigationCost: '',
      specialisedInvestigationDetails: '',
      medicinesSurgeryCost: '',
      totalCost: 0
    });
    clearSignature();
    clearApplicantSignature();
    setSuccess(false);
    setTokenNumber('');
    setErrors({});
  };

  if (success) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="flex justify-center mb-6">
                <CheckCircle className="w-20 h-20 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                सफलतापूर्वक जमा किया गया!<br />
                Successfully Submitted!
              </h2>
              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <p className="text-gray-600 mb-2">
                  आपका टोकन नंबर / Your Token Number
                </p>
                <p className="text-4xl font-bold text-blue-600 mb-4">
                  {tokenNumber}
                </p>
                <p className="text-sm text-gray-500">
                  यह नंबर व्हाट्सएप पर भेज दिया गया है<br />
                  This number has been sent to WhatsApp
                </p>
              </div>

              {/* PDF Download Button */}
              {pdfUrl && (
                <div className="mb-6">
                  <a
                    href={`http://3.6.79.46:8000${pdfUrl}`}
                    download
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors shadow-lg hover:shadow-xl"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download PDF / पीडीएफ डाउनलोड करें
                  </a>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={resetForm}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  नया आवेदन भरें / Fill New Application
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  होम पर जाएं / Go to Home
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="text-center mb-4">
              <p className="text-sm text-gray-500 mb-2">नासांक / Annexure 1</p>
              <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-1">
                ट्रॉमा सेंटर अस्पताल Trauma Centre Hospital
              </h1>
              <p className="text-base md:text-lg text-gray-700 mb-1">
                चिकित्सा विज्ञान संस्थान Institute of Medical Sciences
              </p>
              <p className="text-base md:text-lg text-gray-700 mb-4">
                काशी हिन्दू विश्वविद्यालय Banaras Hindu University
              </p>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-2">
              उपचार का अनुमानित लागत (लगभग)
            </h2>
            <p className="text-center text-gray-600 text-sm md:text-base">
              Estimate of Treatment Cost (Approx)
            </p>
            <p className="text-center text-gray-500 text-xs md:text-sm mt-2">
              (उपचार करने वाले परामर्शदाता के द्वारा भरा जाए / To be filled by the treating consultant)
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            {/* Reference Information Section */}
            {/* <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                संदर्भ जानकारी / Reference Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
            {/* Reference Number */}
            {/* <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    संदर्भ सं / Ref. No.
                  </label>
                  <input
                    type="text"
                    name="refNumber"
                    value={formData.refNumber}
                    onChange={handleInputChange}
                    placeholder="संदर्भ संख्या / Reference Number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div> */}

            {/* Date */}
            {/* <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    दिनांक / Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div> */}

            {/* Mobile Number */}
            {/* <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    मो नं / Mob. No.
                  </label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    placeholder="10 अंकों का मोबाइल नंबर / 10 digit mobile"
                    maxLength="10"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.mobileNumber && (
                    <p className="text-red-500 text-sm mt-1">{errors.mobileNumber}</p>
                  )}
                </div> */}

            {/* Email ID */}
            {/* <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    ई मेल पता / E-mail ID
                  </label>
                  <input
                    type="email"
                    name="emailId"
                    value={formData.emailId}
                    onChange={handleInputChange}
                    placeholder="example@email.com"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.emailId ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.emailId && (
                    <p className="text-red-500 text-sm mt-1">{errors.emailId}</p>
                  )}
                </div>
              </div>
            </div> */}

            {/* Patient Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                रोगी की जानकारी / Patient Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Patient Name */}
                <div className="md:col-span-3">
                  <label className="block text-gray-700 font-semibold mb-2">
                    रोगी का नाम / Patient Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleInputChange}
                    placeholder="पूरा नाम दर्ज करें / Enter full name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.patientName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.patientName && (
                    <p className="text-red-500 text-sm mt-1">{errors.patientName}</p>
                  )}
                </div>

                {/* Age */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    उम्र / Age <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    placeholder="उम्र / Age"
                    min="0"
                    max="150"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.age ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.age && (
                    <p className="text-red-500 text-sm mt-1">{errors.age}</p>
                  )}
                </div>

                {/* Sex */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    लिंग / Sex <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="sex"
                    value={formData.sex}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.sex ? 'border-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">चुनें / Select</option>
                    <option value="male">पुरुष / Male</option>
                    <option value="female">स्त्री / Female</option>
                    <option value="other">अन्य / Other</option>
                  </select>
                  {errors.sex && (
                    <p className="text-red-500 text-sm mt-1">{errors.sex}</p>
                  )}
                </div>

                {/* Father/Husband Name */}
                <div className="md:col-span-3">
                  <label className="block text-gray-700 font-semibold mb-2">
                    पिता/पति का नाम / Name of Father/Husband <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fatherHusbandName"
                    value={formData.fatherHusbandName}
                    onChange={handleInputChange}
                    placeholder="पूरा नाम दर्ज करें / Enter full name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.fatherHusbandName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.fatherHusbandName && (
                    <p className="text-red-500 text-sm mt-1">{errors.fatherHusbandName}</p>
                  )}
                </div>

                {/* Full Address */}
                <div className="md:col-span-3">
                  <label className="block text-gray-700 font-semibold mb-2">
                    पूरा पता / Full Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="fullAddress"
                    value={formData.fullAddress}
                    onChange={handleInputChange}
                    placeholder="पूरा पता दर्ज करें / Enter full address"
                    rows="3"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.fullAddress ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.fullAddress && (
                    <p className="text-red-500 text-sm mt-1">{errors.fullAddress}</p>
                  )}
                </div>

                {/* OPD/Hospital Number */}
                <div className="md:col-span-3">
                  <label className="block text-gray-700 font-semibold mb-2">
                    ओपीडी/अस्पताल संख्या / OPD/Hospital No. <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="opdHospitalNumber"
                    value={formData.opdHospitalNumber}
                    onChange={handleInputChange}
                    placeholder="OPD/Hospital Number"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.opdHospitalNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.opdHospitalNumber && (
                    <p className="text-red-500 text-sm mt-1">{errors.opdHospitalNumber}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Consultant & Diagnosis Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                चिकित्सक और निदान की जानकारी / Consultant & Diagnosis Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Consultant Name */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    चिकित्सक का नाम / Name of Consultant <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="consultantName"
                    value={formData.consultantName}
                    onChange={handleInputChange}
                    placeholder="चिकित्सक का नाम / Consultant Name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.consultantName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.consultantName && (
                    <p className="text-red-500 text-sm mt-1">{errors.consultantName}</p>
                  )}
                </div>

                {/* Unit */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    यूनिट / Unit
                  </label>
                  <input
                    type="text"
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    placeholder="Unit"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Provisional Diagnosis */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    अंतिम निदान / Provisional Diagnosis <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="provisionalDiagnosis"
                    value={formData.provisionalDiagnosis}
                    onChange={handleInputChange}
                    placeholder="निदान विवरण / Diagnosis details"
                    rows="3"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.provisionalDiagnosis ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.provisionalDiagnosis && (
                    <p className="text-red-500 text-sm mt-1">{errors.provisionalDiagnosis}</p>
                  )}
                </div>

                {/* Provisional Date of Operation */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    शल्यकर्म की अनंतिम तिथि / Provisional date of operation
                  </label>
                  <input
                    type="date"
                    name="operationDate"
                    value={formData.operationDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Proposed Operation */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    प्रस्तावित शल्यकर्म (तिथि सहित) / उपचार का तरीका <span className="text-red-500">*</span>
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Proposed operation (with date) / Modality of treatment
                  </p>
                  <textarea
                    name="proposedOperation"
                    value={formData.proposedOperation}
                    onChange={handleInputChange}
                    placeholder="उपचार का विवरण / Treatment details"
                    rows="3"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.proposedOperation ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.proposedOperation && (
                    <p className="text-red-500 text-sm mt-1">{errors.proposedOperation}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Cost Breakdown Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                उपचार की अनुमानित लागत / Estimated approximate cost of treatment
              </h2>

              <div className="space-y-6">
                {/* Investigations Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    (अ) जाँचें / Investigations
                  </h3>

                  <div className="space-y-4">
                    {/* Routine Investigation */}
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        (i) नियमित जाँच / Routine Investigation
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">रु. / ₹</span>
                        <input
                          type="number"
                          name="routineInvestigationCost"
                          value={formData.routineInvestigationCost}
                          onChange={handleInputChange}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Specialised Investigation */}
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        (ii) विशेष-तकनीकी ज़रूरी/गैर-ज़रूरी / Specialised-invasive/non invasive
                      </label>
                      <p className="text-sm text-gray-500 mb-2">
                        (महत्वपूर्ण जाँचों का उल्लेख करें / Mention the important investigations)
                      </p>
                      <textarea
                        name="specialisedInvestigationDetails"
                        value={formData.specialisedInvestigationDetails}
                        onChange={handleInputChange}
                        placeholder="विशेष जाँचों का विवरण / Specialised investigation details"
                        rows="2"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">रु. / ₹</span>
                        <input
                          type="number"
                          name="specialisedInvestigationCost"
                          value={formData.specialisedInvestigationCost}
                          onChange={handleInputChange}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Medicines & Surgery Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    (ब) दवाओं, शल्य प्रक्रिया, संज्ञाहरण व अन्य में व्यय
                  </h3>
                  <p className="text-sm text-gray-500 mb-2">
                    Expenditure in medicines, surgical procedure, Anesthesia & others
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">रु. / ₹</span>
                    <input
                      type="number"
                      name="medicinesSurgeryCost"
                      value={formData.medicinesSurgeryCost}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Total Cost Display */}
                <div className="bg-blue-50 border-2 border-blue-500 p-6 rounded-lg">
                  <div className="flex justify-between items-center">
                    <h3 className="text-2xl font-bold text-gray-800">
                      कुल लागत / Total Cost
                      <p className="text-sm text-yellow-800 text-left">
                        कुल लागत स्वचालित रूप से गणना की जाती है।<br />
                        Total cost is calculated automatically.
                      </p>
                    </h3>
                    <p className="text-3xl font-bold text-blue-600">
                      ₹ {formData.totalCost.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Consultant Signature Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                चिकित्सक के हस्ताक्षर / Consultant Signature <span className="text-red-500">*</span>
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                (मुहर सहित / With Seal)
              </p>

              {/* Mode Selection */}
              <div className="flex gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setSignatureMode('draw');
                    clearSignature();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${signatureMode === 'draw'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <Pen className="w-5 h-5" />
                  Draw Signature / हस्ताक्षर करें
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSignatureMode('upload');
                    clearSignature();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${signatureMode === 'upload'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <ImageIcon className="w-5 h-5" />
                  Upload Image / फ़ोटो अपलोड करें
                </button>
              </div>

              {/* Draw Mode */}
              {signatureMode === 'draw' && (
                <div>
                  <div className={`border-2 rounded-lg overflow-hidden ${errors.consultantSignature ? 'border-red-500' : 'border-gray-300'
                    }`}>
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: 'w-full h-48 bg-white cursor-crosshair',
                        style: { touchAction: 'none' }
                      }}
                      backgroundColor="white"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="flex-1 py-2 px-4 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Clear / साफ़ करें
                    </button>
                    <button
                      type="button"
                      onClick={saveSignature}
                      className="flex-1 py-2 px-4 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Save / सहेजें
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    👆 Draw your signature with seal / मुहर सहित अपना हस्ताक्षर करें
                  </p>
                </div>
              )}

              {/* Upload Mode */}
              {signatureMode === 'upload' && (
                <div>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center ${errors.consultantSignature ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400'
                    }`}>
                    <label className="cursor-pointer">
                      <div className="flex flex-col items-center">
                        <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          Upload signature image (with seal) / मुहर सहित हस्ताक्षर की फ़ोटो अपलोड करें
                        </span>
                        <span className="text-xs text-gray-400 mt-2">
                          JPG, PNG (Max 2MB)
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleSignatureUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Signature Preview */}
              {signatureDataURL && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700 font-semibold mb-2">
                    Signature Preview / हस्ताक्षर पूर्वावलोकन:
                  </p>
                  <div className="bg-white p-4 rounded border border-gray-300 flex justify-center">
                    <img
                      src={signatureDataURL}
                      alt="Consultant Signature"
                      className="max-h-32 object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Remove / हटाएं
                  </button>
                </div>
              )}

              {errors.consultantSignature && (
                <p className="text-red-500 text-sm mt-2">⚠️ {errors.consultantSignature}</p>
              )}
            </div>

            {/* Applicant Signature Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                आवेदक के हस्ताक्षर / Applicant Signature <span className="text-red-500">*</span>
              </h2>

              {/* Mode Selection */}
              <div className="flex gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setApplicantSignatureMode('draw');
                    clearApplicantSignature();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${applicantSignatureMode === 'draw'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <Pen className="w-5 h-5" />
                  Draw Signature
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setApplicantSignatureMode('upload');
                    clearApplicantSignature();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${applicantSignatureMode === 'upload'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <ImageIcon className="w-5 h-5" />
                  Upload Image
                </button>
              </div>

              {/* Draw Mode */}
              {applicantSignatureMode === 'draw' && (
                <div>
                  <div className={`border-2 rounded-lg overflow-hidden ${errors.applicantSignature ? 'border-red-500' : 'border-gray-300'
                    }`}>
                    <SignatureCanvas
                      ref={applicantSigCanvas}
                      canvasProps={{
                        className: 'w-full h-48 bg-white cursor-crosshair',
                        style: { touchAction: 'none', backgroundColor: 'white' }
                      }}
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={clearApplicantSignature}
                      className="flex-1 py-2 px-4 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={saveApplicantSignature}
                      className="flex-1 py-2 px-4 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Save
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Draw your signature above
                  </p>
                </div>
              )}

              {/* Upload Mode */}
              {applicantSignatureMode === 'upload' && (
                <div>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center ${errors.applicantSignature
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-300 hover:border-blue-400'
                    }`}>
                    <label className="cursor-pointer">
                      <div className="flex flex-col items-center">
                        <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Upload signature image</span>
                        <span className="text-xs text-gray-400 mt-2">JPG, PNG (Max 2MB)</span>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleApplicantSignatureUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Signature Preview */}
              {applicantSignatureDataURL && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700 font-semibold mb-2">Signature Preview:</p>
                  <div className="bg-white p-4 rounded border border-gray-300 flex justify-center">
                    <img
                      src={applicantSignatureDataURL}
                      alt="Applicant Signature"
                      className="max-h-32 object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={clearApplicantSignature}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Remove
                  </button>
                </div>
              )}

              {errors.applicantSignature && (
                <p className="text-red-500 text-sm mt-2">{errors.applicantSignature}</p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 py-4 px-6 rounded-lg font-semibold text-white transition-all ${loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                  }`}
              >
                {loading ? 'जमा हो रहा है... / Submitting...' : 'जमा करें / Submit'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 sm:flex-none px-6 py-4 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                रीसेट करें / Reset
              </button>
            </div>

            {/* Footer Note */}
            {/* <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 text-center">
                <span className="font-semibold">Note:</span> <span className="text-red-500">*</span>
                <br />
                All required fields <span className="text-red-500">*</span> must be filled.
              </p>
            </div> */}
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 text-center">
                <span className="font-semibold">नोट / Note:</span> सभी आवश्यक फ़ील्ड (<span className='text-red-500'>*</span>) भरना अनिवार्य है।<br />
                All required fields (<span className='text-red-500'>*</span>) must be filled.
              </p>
            </div>

            {/* Bank Details Section - Bilingual */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-center text-gray-700 mb-2">
                <span className="font-semibold">
                  Bank Draft/Cheque may kindly be prepared in favour of:
                  <br />
                  बैंक ड्राफ्ट/चेक कृपया निम्नलिखित के पक्ष में तैयार किया जाए:
                </span>
              </p>
              <p className="text-center text-base font-bold text-gray-800 mb-1">
                Medical Superintendent, S.S. Hospital, BHU, Varanasi
                <br />
                चिकित्सा अधीक्षक, एस.एस. अस्पताल, बी.एच.यू., वाराणसी
              </p>
              <p className="text-center text-gray-700 mb-3">
                OR-RTGS/NEFT
                <br />
                या आर.टी.जी.एस./एन.ई.एफ.टी.
              </p>
              <div className="border-t border-blue-300 pt-3 mt-3">
                <p className="text-center text-sm text-gray-700">
                  <span className="font-semibold">Bank Name / बैंक का नाम:</span> SBI, BHU,
                  <span className="font-semibold ml-2">IFSC Code / आई.एफ.एस.सी. कोड:</span> SBIN0000211,
                  <span className="font-semibold ml-2">A/c No / खाता संख्या:</span> 10654904247
                </p>
              </div>
            </div>



            {/* Signature Section Info - Updated to show consultant only */}
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-sm text-gray-600">
                <p className="font-semibold text-gray-800 mb-1">
                  चिकित्सक के हस्ताक्षर / Signature of Consultant
                </p>
                <p className="text-xs">
                  (मुहर सहित / With Seal)
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  * Medical Superintendent का counter signature बाद में जोड़ा जाएगा।<br />
                  * Medical Superintendent's counter signature will be added later by admin.
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TreatmentEstimateForm;